import React, { useEffect, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import Tilt from './motion/Tilt';

const CHAR_MS = 20;
const HOLD_MS = 3400;

// variant="spotlight" (default): cinematic single-quote typewriter rotator.
// variant="cards": auto-scrolling marquee of 3D-tilting testimonial cards.
const PhotoTestimonials = ({ items = [], title = 'Loved by Our Queens', subtitle = 'Real Reviews', dark = true, showImages = false, variant = 'spotlight' }) => {
  const reduce = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [chars, setChars] = useState(0);
  const t = items.length ? items[idx % items.length] : null;
  const text = t?.text || '';
  const typingDone = reduce || chars >= text.length;

  useEffect(() => {
    if (variant === 'cards' || reduce || !text) return undefined;
    setChars(0);
    const iv = setInterval(() => {
      setChars((c) => {
        if (c + 1 >= text.length) clearInterval(iv);
        return c + 1;
      });
    }, CHAR_MS);
    return () => clearInterval(iv);
  }, [idx, text, reduce]);

  useEffect(() => {
    if (variant === 'cards' || items.length < 2) return undefined;
    const total = reduce ? 6000 : text.length * CHAR_MS + HOLD_MS;
    const to = setTimeout(() => setIdx((i) => (i + 1) % items.length), total);
    return () => clearTimeout(to);
  }, [idx, items.length, text, reduce]);

  if (!t) return null;
  const fgHead = dark ? 'text-[#f7f3ec]' : 'text-[#1a1a1a]';

  if (variant === 'cards') {
    const loop = [...items, ...items];
    return (
      <section className={`${dark ? 'bg-[#0b0b0b]' : 'bg-[#f2ece0]'} py-16 sm:py-24 overflow-hidden`} data-testid="photo-testimonials">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center mb-12">
          <p className="overline text-gold-gradient mb-3">{subtitle}</p>
          <h2 className={`font-display text-3xl sm:text-5xl ${fgHead}`}>{title}</h2>
          <div className="gold-line w-20 mx-auto mt-5" />
        </div>
        <div className="relative group" style={{ perspective: '1200px' }} data-testid="testimonial-cards-track">
          <div className="marquee-track" style={{ animationDuration: '44s' }}>
            {loop.map((tm, i) => (
              <Tilt key={`${tm.name || 'review'}-${i}`} className="shrink-0 mx-4">
                <div className={`w-[300px] sm:w-[360px] p-7 border ${dark ? 'bg-[#141311] border-[#c6a15b]/20' : 'bg-white border-[#e6ddcd]'}`}>
                  <Quote size={26} className="text-[#c6a15b] mb-3" />
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: tm.rating || 5 }).map((_, s) => (
                      <Star key={s} size={13} className="fill-[#c6a15b] text-[#c6a15b]" />
                    ))}
                  </div>
                  <p className={`font-display italic text-base sm:text-lg leading-relaxed ${dark ? 'text-[#e7e0d3]' : 'text-[#2a2a2a]'}`}>“{tm.text}”</p>
                  <p className="overline text-[#a8823c] text-[0.62rem] mt-5">{tm.name}</p>
                  {tm.location && <p className={`font-body text-xs mt-1 ${dark ? 'text-[#9c9587]' : 'text-[#6b6459]'}`}>{tm.location}</p>}
                </div>
              </Tilt>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const shown = reduce ? text : text.slice(0, chars);
  const fg = fgHead;
  const body = dark ? 'text-[#e7e0d3]' : 'text-[#2a2a2a]';
  const go = (d) => setIdx((i) => (i + d + items.length) % items.length);

  return (
    <section className={`${dark ? 'bg-[#0b0b0b]' : 'bg-[#f2ece0]'} py-16 sm:py-24 overflow-hidden`} data-testid="photo-testimonials">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center mb-12">
        <p className="overline text-gold-gradient mb-3">{subtitle}</p>
        <h2 className={`font-display text-3xl sm:text-5xl ${fg}`}>{title}</h2>
        <div className="gold-line w-20 mx-auto mt-5" />
      </div>

      <div className="relative max-w-2xl mx-auto px-10 sm:px-16 text-center" data-testid="spotlight-review">
        <Quote size={38} className="text-[#c6a15b] mx-auto mb-7" />
        <p className={`font-display italic text-xl sm:text-3xl leading-relaxed ${body} min-h-[10rem] sm:min-h-[9rem]`} data-testid="spotlight-review-text">
          “{shown}
          {!typingDone && <span className="inline-block w-[3px] h-[0.95em] align-middle ml-0.5 bg-[#c6a15b] animate-pulse" aria-hidden="true" />}
          {typingDone && '”'}
        </p>
        <motion.div animate={{ opacity: typingDone ? 1 : 0, y: typingDone ? 0 : 10 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center justify-center gap-1 mt-2">
            {Array.from({ length: t.rating || 5 }).map((_, i) => (
              <Star key={i} size={15} className="fill-[#c6a15b] text-[#c6a15b]" />
            ))}
          </div>
          <p className="overline text-[0.68rem] text-[#a8823c] mt-4" data-testid="spotlight-review-name">{t.name}{t.location ? ` · ${t.location}` : ''}</p>
        </motion.div>

        {items.length > 1 && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous review" data-testid="spotlight-review-prev"
              className={`absolute left-0 top-1/2 -translate-y-1/2 p-2 border ${dark ? 'border-[#c6a15b]/40 text-[#e7d3a1] hover:bg-white/10' : 'border-[#c6a15b]/50 text-[#a8823c] hover:bg-[#0b0b0b] hover:text-[#e7d3a1]'} transition-colors rounded-full`}>
              <ChevronLeft size={17} />
            </button>
            <button onClick={() => go(1)} aria-label="Next review" data-testid="spotlight-review-next"
              className={`absolute right-0 top-1/2 -translate-y-1/2 p-2 border ${dark ? 'border-[#c6a15b]/40 text-[#e7d3a1] hover:bg-white/10' : 'border-[#c6a15b]/50 text-[#a8823c] hover:bg-[#0b0b0b] hover:text-[#e7d3a1]'} transition-colors rounded-full`}>
              <ChevronRight size={17} />
            </button>
            <div className="flex items-center justify-center gap-2 mt-8">
              {items.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Review ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-7 bg-[#c6a15b]' : `w-1.5 ${dark ? 'bg-white/25' : 'bg-[#0b0b0b]/20'}`}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PhotoTestimonials;
